/**
 * SEO Page Metadata Configuration
 *
 * Maps route patterns to their respective HTML titles and meta descriptions.
 * Supports React Router path pattern matching using colon placeholders (e.g. /profile/:id).
 */
export const pageMetadata = {
  // Public/Auth routes
  "/": {
    title: "Login | SelectEzi - Premium Business Network",
    description:
      "Log in to SelectEzi to connect with thousands of local shops, explore business opportunities, and manage your network.",
  },
  "/signup": {
    title: "Create Account | SelectEzi - Premium Business Network",
    description:
      "Join SelectEzi today. Establish your business profile, list your shop, and connect with customers in your area.",
  },
  "/forgot": {
    title: "Forgot Password | SelectEzi",
    description:
      "Recover your SelectEzi account password and regain access to your business network.",
  },
  "/verification": {
    title: "Verify Account | SelectEzi",
    description:
      "Enter your verification details to secure your SelectEzi profile and unlock networking features.",
  },
  "/reset": {
    title: "Reset Password | SelectEzi",
    description: "Choose a new, secure password for your SelectEzi account.",
  },
  "/email-verify": {
    title: "Verify Email | SelectEzi",
    description:
      "Verify your email address to activate your SelectEzi business profile.",
  },
  "/verify-code/:email": {
    title: "Verify Verification Code | SelectEzi",
    description:
      "Verify your code to securely reset your password or activate your email.",
  },

  // Main / Dashboards
  "/dashboard": {
    title: "Dashboard | SelectEzi",
    description:
      "View your business statistics, recent interactions, and connections at a glance.",
  },
  "/BuyingSide": {
    title: "Explore & Buy | SelectEzi Business Directory",
    description:
      "Discover local products, manufacturers, wholesalers, and services. Connect and buy from verified suppliers.",
  },
  "/SellingSide": {
    title: "Manage Sales | SelectEzi Seller Portal",
    description:
      "Manage your incoming inquiries, purchase orders, products, services, and live client chats.",
  },
  "/business-dashboard": {
    title: "Business Dashboard | SelectEzi",
    description:
      "Analyze your business performance, manage your listing catalog, and adjust settings.",
  },
  "/agent-dashboard": {
    title: "Marketer Dashboard | SelectEzi",
    description:
      "Track client sign-ups, active subscription zones, bank links, and monthly payout statistics.",
  },

  // Search/Filters (Buying Side Categories)
  "/find-job": {
    title: "Search Jobs | SelectEzi Careers",
    description:
      "Find verified job listings, contract work, and opportunities posted by local businesses.",
  },
  "/find-candidates": {
    title: "Hire Candidates | SelectEzi Careers",
    description:
      "Browse available candidate resumes, filter by skills and location, and hire top talent.",
  },
  "/find-services": {
    title: "Find Local Services | SelectEzi Directory",
    description:
      "Search and hire verified service providers, experts, and independent contractors in your area.",
  },
  "/find-property": {
    title: "Commercial & Personal Properties | SelectEzi",
    description:
      "Browse commercial business properties and personal real estate listings for sale or rent.",
  },
  "/find-Transport": {
    title: "Logistics & Transport Services | SelectEzi",
    description:
      "Find transport vehicles, shipping agents, and local logistics solutions for your business.",
  },
  "/buy-Minerals": {
    title: "Gems & Minerals Market | SelectEzi Raw Materials",
    description:
      "Connect with mining companies and buy raw minerals, gems, and mined goods directly.",
  },
  "/buy-new-products": {
    title: "Shop New Products | SelectEzi Retail",
    description:
      "Buy brand-new products and local goods directly from verified retail shops in your area.",
  },
  "/buy-used-products": {
    title: "Shop Used Products | SelectEzi Marketplace",
    description:
      "Discover affordable pre-owned goods and verified second-hand items from local sellers.",
  },
  "/buy-homemade-products": {
    title: "Homemade & Handcrafted Goods | SelectEzi",
    description:
      "Explore unique handcrafted items, custom creations, and home-cooked foods from local creators.",
  },

  // Users & Connections
  "/user-profile": {
    title: "My Profile | SelectEzi",
    description:
      "View and update your personal info, address details, and trust score.",
  },
  "/view-friends": {
    title: "My Network Connections | SelectEzi",
    description:
      "Manage your active connections, pending requests, and view profiles of other businesses.",
  },
  "/OrderHistory": {
    title: "Order History | SelectEzi",
    description:
      "Review your purchase history, active order statuses, and receipts.",
  },
  "/chatbot": {
    title: "AI Support Assistant | SelectEzi Help",
    description:
      "Ask questions and get instant guidance on how to navigate the SelectEzi network.",
  },
  "/CV-Form": {
    title: "Create Your Professional CV | SelectEzi",
    description:
      "Build a premium, print-ready professional resume to share with hiring businesses.",
  },
  "/select-template": {
    title: "Select Resume Template | SelectEzi",
    description:
      "Pick a clean, modern design for your generated professional curriculum vitae.",
  },
  "/selectezi-helpline": {
    title: "Customer Support & Help Center | SelectEzi",
    description:
      "Contact our helpline team for user inquiries, complaints, and general assistance.",
  },

  // Dynamic Profiles (using path pattern params)
  "/transport-profile/:id": {
    title: "Transport Operator Profile | SelectEzi",
    description:
      "View vehicle fleets, shipping routes, pricing, and ratings for this transport business.",
  },
  "/used-vehicle-profile/:id": {
    title: "Used Vehicle Listing | SelectEzi",
    description:
      "View condition reports, specifications, and contact the seller for this pre-owned vehicle.",
  },
  "/company-profile/:id": {
    title: "Business Company Profile | SelectEzi",
    description:
      "Explore active job vacancies, company highlights, and contact information.",
  },
  "/job-details/:id": {
    title: "Job Opening Details | SelectEzi Careers",
    description:
      "Read job requirements, duties, benefits, and apply directly to the employer.",
  },
  "/view-job-details/:id": {
    title: "View Job Details | SelectEzi Careers",
    description:
      "Examine candidate job requirements, terms, salary scale, and submit applications.",
  },
  "/view-application/:id": {
    title: "View Job Application | SelectEzi Careers",
    description:
      "Review submitted resume files, cover letters, and contact candidate directly.",
  },
  "/homemadeownerprofile/:id": {
    title: "Homemade Creator Shop Profile | SelectEzi",
    description:
      "Explore handmade listing galleries, craft categories, and organic products.",
  },
  "/home-made-owner-profile/:id": {
    title: "Homemade Shop Owner View | SelectEzi",
    description:
      "Manage your homemade products catalog, customer views, and profile details.",
  },
  "/rawmaterial-owner-profile/:id": {
    title: "Raw Material Shop Profile | SelectEzi",
    description:
      "View raw material inventory lists, factory supplies, and contact info.",
  },
  "/wholesaler-owner-profile/:id": {
    title: "Wholesale Supplier Profile | SelectEzi",
    description:
      "Examine wholesale item catalogs, bulk discount terms, and seller contact.",
  },
  "/manufacturer-owner-profile/:id": {
    title: "Manufacturer Factory Profile | SelectEzi",
    description:
      "Browse manufacturer supply catalogs, industrial capabilities, and request quotes.",
  },
  "/recycle-profile/:id": {
    title: "Recycling Business Profile | SelectEzi",
    description:
      "Explore recycled items, metal scrap, plastic feedstock, and waste management services.",
  },
  "/farming-profile/:id": {
    title: "Agricultural Farm Profile | SelectEzi",
    description:
      "View crop listings, farming products, livestock, and agricultural services.",
  },
  "/used-vehicle-businessprofile/:id": {
    title: "Car Dealership Profile | SelectEzi Auto",
    description:
      "Explore certified pre-owned cars, truck listings, dealership location, and reviews.",
  },

  // Business Profiles
  "/wholsaler-bussines-Profile/:id": {
    title: "Wholesale Business Profile | SelectEzi",
    description:
      "Browse wholesale items, pricing lists, business hours, and place bulk inquiries.",
  },
  "/manufacturer-bussines-Profile/:id": {
    title: "Manufacturing Business Profile | SelectEzi",
    description:
      "Explore production lines, industrial equipment, product certifications, and order books.",
  },
  "/NewItem-bussines-Profile/:id": {
    title: "Retail Store Profile | SelectEzi Shopping",
    description:
      "Shop new retail products, check location maps, store reviews, and talk to representatives.",
  },
  "/Used-Items-Bussines-Profile/:id": {
    title: "Used Products Shop | SelectEzi Market",
    description:
      "Browse pre-owned inventory, negotiate rates, and checkout deals.",
  },
  "/rawmaterial-business-profile/:id": {
    title: "Raw Material Business | SelectEzi Supply",
    description:
      "Source raw timber, steel, plastic pellets, chemical ingredients, and minerals.",
  },
  "/services-profile/:id": {
    title: "Service Provider Profile | SelectEzi Services",
    description:
      "Hire this verified expert. Check rates, portfolios, certifications, and reviews.",
  },
};

export const defaultMetadata = {
  title: "SelectEzi - Premium Business Network",
  description:
    "SelectEzi is a premium state-of-the-art business network connecting manufacturers, wholesalers, retailers, services, and candidates. Grow your business and establish connections today.",
};
